<template>
    <button
        @click="!disabled && !loading && onClick && onClick()"
        :type="type"
        :disabled="disabled"
        class="button"
        :aria-label="ariaLabel"
        :class="[
            size && `size_${size}`,
            variant && `variant_${variant}`,
            border && `border_${border}`,
            decoration && `decoration_${decoration}`,
            color && `color_${color}`,
            font && `font_${font}`,
            iconPosition && icon && `icon_position_${iconPosition}`,
            icon && 'icon',
            loading && 'loading',
            hasSlot && 'slot',
            noPadding && 'noPadding',
        ]">
        <Loading v-if="loading" type="dots" size="xs" color="white" :noPadding="true" />

        <Icon
            v-if="icon"
            :type="icon"
            :rotate="iconRotate"
            :size="iconSize"
            :class="{ 'icon-loading': loading }"
            class="icon" />

        <span
            v-if="hasSlot"
            class="label"
            :class="{ 'label-loading': loading, position: automaticPosition }">
            <slot />
        </span>
    </button>
</template>

<script lang="ts">
    import { PropType, defineComponent, computed, useSlots } from 'vue';
    import { isEmptySlot } from '@shared/helpers/slot';
    import {
        Size,
        Variant,
        Type,
        Color,
        Border,
        Font,
        IconPosition,
        Decoration,
    } from './Button.interfaces';
    import { Type as IconType, Size as IconSize } from '@ui/components/Icon/Icon.interfaces';
    import Loading from '@ui/components/Loading/Loading.vue';
    import Icon from '@ui/components/Icon/Icon.vue';

    export const buttonProps = {
        onClick: Function,
        iconRotate: Number,
        font: {
            type: String as PropType<Font>,
            default: 'font-button-medium',
        },
        border: { type: String as PropType<Border>, default: 'radius' },
        type: { type: String as PropType<Type>, default: 'button' },
        color: { type: String as PropType<Color>, default: 'primary' },
        size: { type: String as PropType<Size>, default: 'm' },
        disabled: { type: Boolean, default: false },
        noPadding: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        automaticPosition: { type: Boolean, default: true },
        icon: { type: String as PropType<IconType> },
        iconPosition: {
            type: String as PropType<IconPosition>,
            default: 'right',
        },
        variant: {
            type: String as PropType<Variant>,
            default: 'filled',
        },
        decoration: {
            type: String as PropType<Decoration | null>,
            default: null,
        },
        ariaLabel: {
            type: String,
        },
    };

    export default defineComponent({
        name: 'Button',
        components: { Loading, Icon },
        props: buttonProps,
        setup(props) {
            const slots = useSlots();

            const hasSlot = computed(() => !isEmptySlot(slots.default));

            const getIconSize = (): IconSize => {
                switch (props.size) {
                    case 'm':
                    case 'l':
                        return 's';
                    default:
                        return props.size;
                }
            };

            return {
                hasSlot,
                iconSize: computed(() => getIconSize()),
            };
        },
    });
</script>

<style lang="scss">
    @forward './Button.scss';
</style>
